import { PlusCircle } from "lucide-react";
import { useState } from "react";
import { useMatch } from "react-router-dom";

import { MenuItemType } from "@/client";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useTranslation } from "react-i18next";

const menuItemTypes: { value: MenuItemType; label: string }[] = [
  {
    value: MenuItemType.CONTENT,
    label: "Content",
  },
  {
    value: MenuItemType.NEWS,
    label: "News",
  },
  {
    value: MenuItemType.LINK,
    label: "Link",
  },
  {
    value: MenuItemType.CUSTOMIZED_PAGE,
    label: "Link with QR gate",
  },
  {
    value: MenuItemType.SCHEDULE,
    label: "Schedule",
  },
  {
    value: MenuItemType.MY_SCHEDULE,
    label: "My Schedule",
  },
  {
    value: MenuItemType.SEARCH,
    label: "Search",
  },
  {
    value: MenuItemType.NOTES,
    label: "My Notes",
  },
  {
    value: MenuItemType.NOW,
    label: "What's on now",
  },
  {
    value: MenuItemType.PEOPLE,
    label: "People",
  },
];

const menuItemTypeTranslationKeys: Record<MenuItemType, string> = {
  [MenuItemType.CONTENT]: "navItem.content",
  [MenuItemType.NEWS]: "navItem.news",
  [MenuItemType.LINK]: "navItem.link",
  [MenuItemType.CUSTOMIZED_PAGE]: "navItem.linkWithQrGate",
  [MenuItemType.SCHEDULE]: "navItem.schedule",
  [MenuItemType.MY_SCHEDULE]: "navItem.mySchedule",
  [MenuItemType.SEARCH]: "navItem.search",
  [MenuItemType.NOTES]: "navItem.myNotes",
  [MenuItemType.NOW]: "navItem.whatsOnNow",
  [MenuItemType.PEOPLE]: "navItem.people",
  [MenuItemType.FLOORPLAN]: "navItem.floorplan",
  [MenuItemType.POLL]: "navItem.poll",
  [MenuItemType.SPONSORS]: "navItem.sponsors",
  [MenuItemType.SETTINGS]: "navItem.settings",
};

type AddNavigationItemProps = {
  onSelect: (type: MenuItemType) => void;
};

function AddNavigationItem(props: AddNavigationItemProps) {
  const { t } = useTranslation();
  const { onSelect } = props;
  const [open, setOpen] = useState(false);
  const isSettingsPage = useMatch("/settings") !== null;

  const availableMenuItems = isSettingsPage
    ? menuItemTypes.filter(
        (item) =>
          item.value === MenuItemType.CONTENT ||
          item.value === MenuItemType.LINK ||
          item.value === MenuItemType.NEWS,
      )
    : menuItemTypes;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          size="icon"
          variant="ghost"
          className="size-7 rounded-full p-1"
          aria-expanded={open}
          role="combobox"
        >
          <PlusCircle className="size-3.5" />
          <span className="sr-only">{t("navItem.addItem")}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[200px] p-0">
        <Command>
          <CommandInput placeholder={t("navItem.searchType")} />
          <CommandEmpty>{t("navItem.noType")}</CommandEmpty>
          <CommandGroup>
            <CommandList>
              {availableMenuItems.map((menuItemType) => (
                <CommandItem
                  key={menuItemType.value}
                  value={menuItemType.value}
                  onSelect={() => {
                    onSelect(menuItemType.value);
                    setOpen(false);
                  }}
                >
                  {t(menuItemTypeTranslationKeys[menuItemType.value])}
                </CommandItem>
              ))}
            </CommandList>
          </CommandGroup>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export { AddNavigationItem };
